import { CopyAction } from '../../ui/action-feedback.js';
import { Mail, Phone } from 'lucide-react';
import type { ClientContact } from './client-contacts.js';
import { InstagramIcon as Instagram } from './InstagramIcon.js';
import './CrmShootEditor.css';

export function ClientContactRows({
  contacts,
  international,
}: {
  contacts: readonly ClientContact[];
  international: string | null;
}) {
  return (
    <div className="shoot-client__contacts">
      {[...contacts]
        .sort(
          (a, b) =>
            ['Instagram', 'Phone', 'Email'].indexOf(a.label) -
            ['Instagram', 'Phone', 'Email'].indexOf(b.label),
        )
        .map((item) => (
          <div key={item.label} className="shoot-client__contact-row">
            <ShootContactValue contact={item} />
            <div className="shoot-client__contact-actions">
              {item.label === 'Phone' &&
                (international ? (
                  <>
                    <a
                      className="shoot-button shoot-button--outline shoot-button--icon"
                      aria-label="WhatsApp"
                      title="WhatsApp"
                      href={`https://wa.me/${international}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <ContactServiceIcon service="whatsapp" />
                    </a>
                    <a
                      className="shoot-button shoot-button--outline shoot-button--icon"
                      aria-label="Telegram"
                      title="Telegram"
                      href={`https://t.me/+${international}`}
                      aria-description="Opens a phone link; a Telegram account is not confirmed."
                      target="_blank"
                      rel="noreferrer"
                    >
                      <ContactServiceIcon service="telegram" />
                    </a>
                  </>
                ) : (
                  <>
                    <button
                      className="shoot-button shoot-button--outline shoot-button--icon"
                      disabled
                      aria-label="WhatsApp"
                      title="Add an international country code to enable WhatsApp"
                    >
                      <ContactServiceIcon service="whatsapp" />
                    </button>
                    <button
                      className="shoot-button shoot-button--outline shoot-button--icon"
                      disabled
                      aria-label="Telegram"
                      title="Add an international country code to enable Telegram"
                    >
                      <ContactServiceIcon service="telegram" />
                    </button>
                  </>
                ))}
              <CopyAction label={item.label} value={item.value} />
            </div>
          </div>
        ))}
    </div>
  );
}

function ShootContactValue({ contact }: { contact: ClientContact }) {
  const content = (
    <>
      {contact.label === 'Email' ? (
        <Mail size={17} aria-hidden="true" />
      ) : contact.label === 'Phone' ? (
        <Phone size={17} aria-hidden="true" />
      ) : (
        <Instagram size={17} aria-hidden="true" />
      )}
      <span>{contact.value}</span>
    </>
  );
  return contact.href && contact.label !== 'Phone' ? (
    <a
      className="shoot-client__contact-value"
      href={contact.href}
      {...(contact.label === 'Instagram' ? { target: '_blank', rel: 'noreferrer' } : {})}
    >
      {content}
    </a>
  ) : (
    <span className="shoot-client__contact-value">{content}</span>
  );
}

function ContactServiceIcon({ service }: { service: 'whatsapp' | 'telegram' }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      {service === 'telegram' ? (
        <path d="M21.4 3.6 18.2 20c-.2 1.1-.9 1.4-1.8.9l-4.9-3.6-2.4 2.3c-.3.3-.5.5-1 .5l.4-5 9-8.1c.4-.4-.1-.6-.6-.3L5.8 13.7 1 12.2c-1-.3-1-1 .2-1.5L20 3.4c.9-.3 1.6.2 1.4.2Z" />
      ) : (
        <path d="M12 2a10 10 0 0 0-8.7 14.9L2 22l5.2-1.3A10 10 0 1 0 12 2Zm0 18a8 8 0 0 1-4.1-1.1l-.3-.2-3 .8.8-3-.2-.3A8 8 0 1 1 12 20Zm4.5-5.7c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.5.1l-.8 1c-.1.2-.3.2-.5.1-1.4-.6-2.5-1.6-3.2-2.8-.2-.3 0-.4.1-.5l.5-.6.2-.4c.1-.2 0-.3 0-.5l-.8-1.9c-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.7.7-1 1.5-.9 2.4.2 1.2.7 2.3 1.5 3.2 1.4 1.8 3.2 3.1 5.4 3.6.7.2 1.5.1 2.1-.2.6-.3 1-1 1.1-1.7.1-.3.1-.5 0-.6l-.7-.3Z" />
      )}
    </svg>
  );
}
